import React, { ReactNode, CSSProperties } from 'react';
import { List, Typography, Box, Divider } from '@mui/material';

/**
 * ConfigurableList - A simple list component that renders sections with any ReactNode content
 */

export interface ListSection {
    title?: string;
    rows: ReactNode[];
    component?: () => ReactNode;
    onClick?: () => void;
    showDivider?: boolean;
    headerStyle?: CSSProperties;
    hideHeader?: boolean;
}

export interface ConfigurableListProps {
    sections: ListSection[];
    containerStyle?: CSSProperties;
    textColor?: string;
}

const ConfigurableList = ({
    sections,
    containerStyle,
    textColor = '#FFFFFF',
}: ConfigurableListProps) => {

    const renderSectionHeader = (section: ListSection) => {
        if (section.hideHeader) {
            return null;
        }

        if (section.component) {
            return <Box>{section.component()}</Box>;
        }

        if (section.title) {
            return (
                <Typography
                    variant="subtitle2"
                    style={{
                        color: textColor,
                        padding: '16px 16px 8px 16px',
                        fontWeight: 600,
                        cursor: section.onClick ? 'pointer' : 'default',
                        ...section.headerStyle
                    }}
                    onClick={section.onClick}
                >
                    {section.title}
                </Typography>
            );
        }

        return null;
    };

    return (
        <Box style={containerStyle}>
            <List disablePadding>
                {sections.map((section, sectionIndex) => (
                    <Box key={section.title || `section-${sectionIndex}`}>
                        {renderSectionHeader(section)}

                        {section.rows}

                        {section.showDivider && (
                            <Divider
                                style={{
                                    margin: '16px 0',
                                    backgroundColor: 'rgba(255, 255, 255, 0.12)'
                                }}
                            />
                        )}
                    </Box>
                ))}
            </List>
        </Box>
    );
};

export default ConfigurableList;
